import { SharedApiV2 } from "@capstone/mock-api";
import { getSiteRouteUrl, Site, SiteReference } from "src/app/core/sites";
import { getApiDetailsDecorator, groupBy } from "src/utils";

const api = getApiDetailsDecorator<SharedApiV2.DoorGroupSelectedListDto>();

interface DeserializeArguments {
  readonly site: Site;
}

export class DoorGroup {
  private constructor(args: ClassProperties<DoorGroup>) {
    this.id = args.id;
    this.name = args.name;
    this.site = args.site;
  }

  @api() public readonly id: number;
  @api() public readonly name: string;
  public readonly site: Site;

  public static deserialize(
    data: Omit<SharedApiV2.DoorGroupSelectedListDto, "siteID">,
    { site }: DeserializeArguments,
  ): DoorGroup {
    return new DoorGroup({
      id: data.id,
      name: data.name,
      site,
    });
  }

  public static deserializeList(
    value: SharedApiV2.DoorGroupSelectedListDto[],
    args: DeserializeArguments,
  ): readonly DoorGroup[] {
    return value.map((group) => DoorGroup.deserialize(group, args));
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getDoorGroupRouteUrl(this.site, this, ...childPaths);
  }
}

export function getDoorGroupRouteUrl(
  site: SiteReference,
  doorGroup: Pick<DoorGroup, "id">,
  ...childPaths: string[]
): string {
  return getSiteRouteUrl(
    site,
    "settings/door-groups",
    String(doorGroup.id),
    ...childPaths,
  );
}

/** `/DoorGroups/SelectedList` is partner-scoped, so a response can span sites. */
export function groupDoorGroupOptionsBySite(
  value: readonly SharedApiV2.DoorGroupSelectedListDto[],
): ReadonlyMap<number, ReadonlyArray<Pick<DoorGroup, "id" | "name">>> {
  return new Map(
    Array.from(
      groupBy(value, ({ siteID }) => siteID),
      ([siteId, groups]): [
        number,
        ReadonlyArray<Pick<DoorGroup, "id" | "name">>,
      ] => [siteId, groups.map(({ id, name }) => ({ id, name }))],
    ),
  );
}
